import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, User, ArrowRight, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from 'pages/HomePage/components/Header';
import Footer from 'pages/HomePage/components/Footer';
import { usePublishedArticles } from 'hooks/useArticles';
import { useRevealAnimation } from 'hooks/useRevealAnimation';
import { Article } from 'types/article';
import './TinTucPage.scss';

const PAGE_SIZE = 6;

const formatDate = (iso: string, lang: string) =>
  new Intl.DateTimeFormat(lang === 'en' ? 'en-US' : 'vi-VN', {
    day: 'numeric', month: 'long', year: 'numeric',
  }).format(new Date(iso));

const ThumbnailFallback: React.FC<{ title: string }> = ({ title }) => (
  <div className="nt-card__img-fallback">
    <span>{title.charAt(0).toUpperCase()}</span>
  </div>
);

const ArticleCard: React.FC<{ art: Article; lang: string }> = ({ art, lang }) => (
  <article className="nt-card">
    <div className="nt-card__img-wrap">
      {art.thumbnail
        ? <img src={art.thumbnail} alt={art.title} className="nt-card__img" loading="lazy" />
        : <ThumbnailFallback title={art.title} />
      }
    </div>
    <div className="nt-card__body">
      <div className="nt-card__meta">
        <span className="nt-card__meta-item">
          <Calendar size={12} />
          {formatDate(art.createdAt, lang)}
        </span>
        <span className="nt-card__meta-item">
          <User size={12} />
          {art.author}
        </span>
      </div>
      <h2 className="nt-card__title">{art.title}</h2>
      <p className="nt-card__excerpt">{art.excerpt}</p>
      <Link to={`/tin-tuc/${art.slug}`} className="nt-card__read-more">
        Đọc thêm <ArrowRight size={14} />
      </Link>
    </div>
  </article>
);

const TinTucGHTMPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(1);
  const { data: allArticles, loading, error } = usePublishedArticles('MKL');
  useRevealAnimation();

  const articles = allArticles ?? [];
  const totalPages = Math.ceil(articles.length / PAGE_SIZE);
  const pagedArticles = articles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <Header />
      <main>
        {/* ── Hero */}
        <section className="nt-hero" aria-label={t('tinTuc.hero.ariaGhtm')}>
          <div className="nt-hero__inner">
            <span className="nt-hero__eyebrow">{t('tinTuc.hero.eyebrow')}</span>
            <h1 className="nt-hero__title">{t('tinTuc.hero.titleGhtm')}</h1>
            <p className="nt-hero__sub">{t('tinTuc.hero.subtitleGhtm')}</p>
          </div>
        </section>

        {/* ── Article grid */}
        <section className="nt-grid">
          {loading && (
            <div className="nt-state nt-state--loading">
              <Loader2 className="nt-state__spinner" size={32} />
              <span>Đang tải bài viết…</span>
            </div>
          )}

          {!loading && error && (
            <div className="nt-state nt-state--error">
              <p>Không thể tải bài viết. Vui lòng thử lại sau.</p>
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="nt-grid__list">
                {pagedArticles.length === 0
                  ? <p className="nt-state nt-state--empty">Chưa có bài viết nào.</p>
                  : pagedArticles.map(art => (
                    <ArticleCard key={art.id} art={art} lang={i18n.language} />
                  ))
                }
              </div>

              {/* ── Pagination */}
              {totalPages > 1 && (
                <div className="nt-pagination">
                  <button
                    className="nt-pagination__btn"
                    onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    disabled={page === 1}
                    aria-label={t('tinTuc.pagination.prev')}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={`nt-pagination__btn${page === i + 1 ? ' nt-pagination__btn--active' : ''}`}
                      onClick={() => { setPage(i + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      aria-label={`${t('tinTuc.pagination.page')} ${i + 1}`}
                      aria-current={page === i + 1 ? 'page' : undefined}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className="nt-pagination__btn"
                    onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    disabled={page === totalPages}
                    aria-label={t('tinTuc.pagination.next')}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
};

export default TinTucGHTMPage;
